import React, { useEffect, useState } from "react";
import { IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useLocation, useNavigate } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import { useLazyEditHistoryQuery } from "../../service/booking";
import { showError } from "../../constants/toasts";
import Loader from "../../constants/Loader";
import { OFFER_TYPE } from "../../utils/enums";
import { AuditLog } from "../../types/General";
import { formatDubaiDateTime } from "../../utils/dateTime";
import { formatAEDNumber } from "../../utils/validations";

const EditHistory: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const bookingId = state?.bookingId || "";
  const offerType = state?.offerType || "";
  const name = state?.name || "";
  const email = state?.email || "";
  const phone = state?.phone || "";
  const countryCode = state?.countryCode || "";
  const dateCreated = state?.dateCreated || "";
  const category = state?.category || "";
  const offerName = state?.offerName || "";
  const dietary = state?.dietary || "";
  const employeeName = state?.employeeName || "";
  const hashId = state?.hashId || "";

  const [editHistory] = useLazyEditHistoryQuery();
  const goBack = () => navigate(-1);
  const [historyData, setHistoryData] = useState<AuditLog[]>([]);

  const [loading, setLoading] = useState(false);



  const getEditHistoryApi = async () => {
    setLoading(true);
    try {
      const response = await editHistory({ bookingId }).unwrap();
      setLoading(false);

      if (response?.status === 200) {
        const logs = response?.response || [];

        if (logs?.length > 0) {
          const firstItem = logs[0];
          const baseline: AuditLog = {
            ...firstItem,
            editedAt: dateCreated,
            changes: Object.fromEntries(
              Object.entries(firstItem?.changes || {})?.map(([key, value]: any) => [
                key,
                {
                  ...value,
                  IsChanged: false,
                  NewValue: value?.OldValue,
                  OldValue: value?.OldValue,
                },
              ])
            ),
          };


          setHistoryData([...logs, baseline]);
        } else {
          setHistoryData([]);
        }
      } else {
        setHistoryData([]);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  useEffect(() => {
    getEditHistoryApi();
  }, []);

  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_title d_flex">
        <div className="left_s">
          <h1 className="back_arrow">
            <IconButton onClick={goBack}>
              <ArrowBackIcon />
            </IconButton>
            View Edit History - {offerType}
          </h1>
        </div>
      </div>

      {offerType === OFFER_TYPE.EVENTS && historyData?.length
        ? historyData?.map((item) => (
          <>
            <h3>
              {item?.editedAt ? formatDubaiDateTime(item?.editedAt) : "-"}
            </h3>

            <div className="cards">
              <div className="gap_p view_page">

                <div className="w_25">
                  <h6>Booking ID</h6>
                  <p>{hashId || "-"}</p>
                </div>


                <div className="w_25">
                  <h6>Client Name</h6>
                  <p>
                    {name || ""}
                  </p>
                </div>

                <div className="w_25">
                  <h6>Client Email</h6>
                  <p>{email || "-"}</p>
                </div>

                <div className="w_25">
                  <h6>Client Phone</h6>
                  <p>{`${countryCode ? `+${countryCode}` : ""} ${phone || "-"}`}</p>
                </div>

                <div className="w_25">
                  <h6>Employee Name</h6>
                  <p>{employeeName || "-"}</p>
                </div>

                <div className="w_25">
                  <h6>Category</h6>
                  <p>{category || "-"}</p>
                </div>

                <div className="w_25">
                  <h6>Offer Name</h6>
                  <p>{offerName || "-"}</p>
                </div>

                <div className="w_25">
                  <h6>Date Created</h6>
                  <p>{dateCreated ? formatDubaiDateTime(dateCreated) : "-"}</p>
                </div>

                <div className="w_25">
                  <h6>Dietary</h6>
                  <p>{dietary?.length ? dietary?.map((i: any) => i?.name).join(" , ") : "-"}</p>
                </div>


                <div className="w_25">
                  <h6>Guests</h6>
                  <p style={{ color: item?.changes?.NumberOfGuest?.IsChanged ? "green" : "white" }}>
                    {item?.changes?.NumberOfGuest?.IsChanged ? item?.changes?.NumberOfGuest?.NewValue : item?.changes?.NumberOfGuest?.OldValue || '0'}
                  </p>
                </div>

                <div className="w_25">
                  <h6>Special Request</h6>
                  <p style={{ color: item?.changes?.SpecialRequest?.IsChanged ? "green" : "white" }}>{item?.changes?.SpecialRequest?.IsChanged ? item?.changes?.SpecialRequest?.NewValue : item?.changes?.SpecialRequest?.OldValue || "-"}</p>
                </div>



                <div className="w_25">
                  <h6>Booking Date</h6>
                  <p style={{ color: item?.changes?.BookingDate?.IsChanged ? "green" : "white" }}>{item?.changes?.BookingDate?.IsChanged ? formatDubaiDateTime(item?.changes?.BookingDate?.NewValue) : formatDubaiDateTime(item?.changes?.BookingDate?.OldValue) || "-"}</p>
                </div>



                {item?.changes?.Answers?.Updated?.map((answer, index) => {
                  const showNew = answer?.IsChanged;
                  const activeAnswer = showNew ? answer?.NewAnswer : answer?.OldAnswer;


                  let displayValue = "";

                  switch (answer?.InputType) {
                    case "Checkbox":
                      displayValue =
                        activeAnswer?.Checkboxes?.length > 0
                          ? activeAnswer?.Checkboxes?.join(", ")
                          : "Not Selected";
                      break;

                    case "Radio":
                      displayValue = activeAnswer?.Selected || "Not Selected";
                      break;

                    case "Text":
                      displayValue = activeAnswer?.Text || "Not Provided";
                      break;

                    case "Date":
                      displayValue = activeAnswer?.Text
                        ? formatDubaiDateTime(activeAnswer?.Text)
                        : "No Date Selected";
                      break;

                    default:
                      displayValue = "N/A";
                  }

                  return (
                    <div key={index} className="control_group w_25">
                      <h6>{answer?.Question}</h6>
                      <p style={{ color: showNew ? "green" : "white" }}>{displayValue}</p>
                    </div>
                  );
                })}



              </div>

              {item?.changes?.Partner?.IsChanged && item?.changes?.Partner?.New ? (
                <>
                  <div className="control_group w_100">
                    <hr />
                  </div>
                  <div className="section_title mb_20">
                    <h2>
                      Partner Detail{" "}
                      <span className="count" style={{ color: "green" }}>
                        ( Added )
                      </span>
                    </h2>
                  </div>

                  <div className="gap_p view_page">
                    <div className="control_group w_25">
                      <h6>Name</h6>
                      <p>
                        {item?.changes?.Partner?.New?.Name ||
                          "-"}
                      </p>
                    </div>

                    <div className="control_group w_25">
                      <h6>Email</h6>
                      <p>
                        {item?.changes?.Partner?.New?.Email ||
                          "-"}
                      </p>
                    </div>

                    <div className="control_group w_25">
                      <h6>Dietary Requirement</h6>
                      <p>   {item?.changes?.Partner?.New?.Dietary?.length
                        ? item?.changes?.Partner?.New?.Dietary?.map((i: any) => i).join(" , ")
                        : "-"}</p>
                    </div>

                    <div className="control_group w_25">
                      <h6>Payment Status</h6>
                      <p>
                        <strong>AED {item?.changes?.Partner?.New?.Amount ? formatAEDNumber(item?.changes?.Partner?.New?.Amount) : "0"}</strong>
                      </p>
                    </div>

                  </div>
                </>
              ) : null}

              {!item?.changes?.Partner?.IsChanged && item?.changes?.Partner?.Old ? (
                <>
                  <div className="control_group w_100">
                    <hr />
                  </div>
                  <div className="section_title mb_20">
                    <h2>
                      Partner Detail{" "}

                    </h2>
                  </div>

                  <div className="gap_p view_page">
                    <div className="control_group w_25">
                      <h6>Name</h6>
                      <p>
                        {item?.changes?.Partner?.New?.Name ||
                          "-"}
                      </p>
                    </div>

                    <div className="control_group w_25">
                      <h6>Email</h6>
                      <p>
                        {item?.changes?.Partner?.New?.Email ||
                          "-"}
                      </p>
                    </div>

                    <div className="control_group w_25">
                      <h6>Dietary Requirement</h6>
                      <p>   {item?.changes?.Partner?.New?.Dietary?.length
                        ? item?.changes?.Partner?.New?.Dietary?.map((i: any) => i).join(" , ")
                        : "-"}</p>
                    </div>

                    <div className="control_group w_25">
                      <h6>Payment Status</h6>
                      <p>
                        <strong>AED {item?.changes?.Partner?.New?.Amount ? formatAEDNumber(item?.changes?.Partner?.New?.Amount) : "0"}</strong>
                      </p>
                    </div>

                  </div>
                </>
              ) : null}

              {item?.changes?.Guests?.IsChanged && item?.changes?.Guests?.Added?.length
                ? item?.changes?.Guests?.Added?.map((guest) => (
                  <>
                    <div className="control_group w_100">
                      <hr />
                    </div>
                    <div className="section_title mb_20">
                      <h2>
                        Guest Detail{" "}
                        <span className="count" style={{ color: "green" }}>
                          ( Added )
                        </span>
                      </h2>
                    </div>

                    <div className="gap_p view_page">
                      <div className="control_group w_25">
                        <h6>Name</h6>
                        <p>{guest?.Name || "-"}</p>
                      </div>
                      <div className="control_group w_25">
                        <h6>Email</h6>
                        <p>{guest?.Email || "-"}</p>
                      </div>
                      <div className="control_group w_25">
                        <h6>Dietary Requirement</h6>
                        <p>
                          {" "}
                          {guest?.Dietary?.length
                            ? guest?.Dietary?.map((i: any) => i).join(" , ")
                            : "-"}
                        </p>
                      </div>
                      <div className="control_group w_25">
                        <h6>Payment</h6>
                        <p>

                          <strong >
                            AED {guest?.Amount || "0"}
                          </strong>
                        </p>
                      </div>

                    </div>
                  </>
                ))
                : null}

              {item?.changes?.Guests?.IsChanged && item?.changes?.Guests?.Removed?.length
                ? item?.changes?.Guests?.Removed?.map((guest) => (
                  <>
                    <div className="control_group w_100">
                      <hr />
                    </div>
                    <div className="section_title mb_20">
                      <h2>
                        Guest Detail{" "}
                        <span className="count" style={{ color: "red" }}>
                          ( Removed )
                        </span>
                      </h2>
                    </div>

                    <div className="gap_p view_page">
                      <div className="control_group w_25">
                        <h6>Name</h6>
                        <p>{guest?.Name || "-"}</p>
                      </div>
                      <div className="control_group w_25">
                        <h6>Email</h6>
                        <p>{guest?.Email || "-"}</p>
                      </div>
                      <div className="control_group w_25">
                        <h6>Dietary Requirement</h6>
                        <p>
                          {" "}
                          {guest?.Dietary?.length
                            ? guest?.Dietary?.map((i: any) => i).join(" , ")
                            : "-"}
                        </p>
                      </div>
                      <div className="control_group w_25">
                        <h6>Payment</h6>
                        <p>

                          <strong>
                            AED {guest?.Amount || "0"}
                          </strong>
                        </p>
                      </div>

                    </div>
                  </>
                ))
                : null}

              {!item?.changes?.Guests?.IsChanged
                ? item?.changes?.Guests?.Old?.map((guest) => (
                  <>
                    <div className="control_group w_100">
                      <hr />
                    </div>
                    <div className="section_title mb_20">
                      <h2>
                        Guest Detail{" "}

                      </h2>
                    </div>

                    <div className="gap_p view_page">
                      <div className="control_group w_25">
                        <h6>Name</h6>
                        <p>{guest?.Name || "-"}</p>
                      </div>
                      <div className="control_group w_25">
                        <h6>Email</h6>
                        <p>{guest?.Email || "-"}</p>
                      </div>
                      <div className="control_group w_25">
                        <h6>Dietary Requirement</h6>
                        <p>
                          {" "}
                          {guest?.Dietary?.length
                            ? guest?.Dietary?.map((i: any) => i).join(" , ")
                            : "-"}
                        </p>
                      </div>
                      <div className="control_group w_25">
                        <h6>Payment</h6>
                        <p>

                          <strong>
                            AED {guest?.Amount || "0"}
                          </strong>
                        </p>
                      </div>

                    </div>
                  </>
                ))
                : null}

            </div>
          </>
        ))
        : null}
      {offerType === OFFER_TYPE.RESTAURANT && historyData?.length ? (
        historyData?.map((item) => (
          <>
            <h3>
              {item?.editedAt ? formatDubaiDateTime(item?.editedAt) : "-"}
            </h3>


            <div className="cards">
              <div className="gap_p view_page">

                <div className="w_25">
                  <h6>Booking ID</h6>
                  <p>{hashId || "-"}</p>
                </div>

                <div className="w_25">
                  <h6>Client Name</h6>
                  <p>
                    {name || ""}
                  </p>
                </div>

                <div className="w_25">
                  <h6>Client Email</h6>
                  <p>{email || "-"}</p>
                </div>

                <div className="w_25">
                  <h6>Client Phone</h6>
                  <p>{`${countryCode ? `+${countryCode}` : ""} ${phone || "-"}`}</p>
                </div>

                <div className="w_25">
                  <h6>Employee Name</h6>
                  <p>{employeeName || "-"}</p>
                </div>

                <div className="w_25">
                  <h6>Category</h6>
                  <p>{category || "-"}</p>
                </div>

                <div className="w_25">
                  <h6>Offer Name</h6>
                  <p>{offerName || "-"}</p>
                </div>

                <div className="w_25">
                  <h6>Date Created</h6>
                  <p>{dateCreated ? formatDubaiDateTime(dateCreated) : "-"}</p>
                </div>

                <div className="w_25">
                  <h6>Dietary</h6>
                  <p>{dietary?.length ? dietary?.map((i: any) => i?.name).join(" , ") : "-"}</p>
                </div>

                <div className="w_25">
                  <h6>Guests</h6>
                  <p style={{ color: item?.changes?.NumberOfGuest?.IsChanged ? "green" : "white" }}>
                    {item?.changes?.NumberOfGuest?.IsChanged ? item?.changes?.NumberOfGuest?.NewValue : item?.changes?.NumberOfGuest?.OldValue || '0'}
                  </p>
                </div>
                {/* <div className="w_25">
                  <h6>Booking Date & Time </h6>
                  <p>{item?.bookingFieldChanges?.BookingDate ? formatDubaiDateTime(item?.bookingFieldChanges?.BookingDate?.New) : '-'}</p>
                </div> */}

                <div className="w_25">
                  <h6>Special Request</h6>
                  <p style={{ color: item?.changes?.SpecialRequest?.IsChanged ? "green" : "white" }}>{item?.changes?.SpecialRequest?.IsChanged ? item?.changes?.SpecialRequest?.NewValue : item?.changes?.SpecialRequest?.OldValue || "-"}</p>
                </div>

                <div className="w_25">
                  <h6>Booking Date</h6>
                  <p style={{ color: item?.changes?.BookingDate?.IsChanged ? "green" : "white" }}>{item?.changes?.BookingDate?.IsChanged ? formatDubaiDateTime(item?.changes?.BookingDate?.NewValue) : formatDubaiDateTime(item?.changes?.BookingDate?.OldValue) || "-"}</p>
                </div>



                {item?.changes?.Answers?.Updated?.map((answer, index) => {
                  const showNew = answer?.IsChanged;
                  const activeAnswer = showNew ? answer?.NewAnswer : answer?.OldAnswer;


                  let displayValue = "";

                  switch (answer?.InputType) {
                    case "Checkbox":
                      displayValue =
                        activeAnswer?.Checkboxes?.length > 0
                          ? activeAnswer?.Checkboxes?.join(", ")
                          : "Not Selected";
                      break;

                    case "Radio":
                      displayValue = activeAnswer?.Selected || "Not Selected";
                      break;

                    case "Text":
                      displayValue = activeAnswer?.Text || "Not Provided";
                      break;

                    case "Date":
                      displayValue = activeAnswer?.Text
                        ? formatDubaiDateTime(activeAnswer?.Text)
                        : "No Date Selected";
                      break;

                    default:
                      displayValue = "N/A";
                  }

                  return (
                    <div key={index} className="control_group w_25">
                      <h6>{answer?.Question}</h6>
                      <p style={{ color: showNew ? "green" : "white" }}>{displayValue}</p>
                    </div>
                  );
                })}



              </div>
            </div>
          </>
        ))
      ) : null}

      {
        !historyData?.length && (
          <h2 className="text-center">No History Found</h2>
        )
      }
    </MainContainer>
  );
};

export default EditHistory;
